import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  Avatar,
  Space,
  Typography,
  Select,
  message,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  RobotOutlined,
  ControlOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { useRobotTypeManagement } from "../hooks/useRobotTypeManagement";
import { useJoyControlMappingManagement } from "../hooks/useJoyControlMappingManagement";
import { servicesUrlConfig } from "shared/config/setting";
import { BUTTON_STYLES } from "../constants";

const { Title, Text } = Typography;
const { TextArea } = Input;

const RobotTypesManagement = () => {
  const { isAuthenticated, authFetch, loading: authLoading } = useAuth();
  const { notifySuccess, notifyError } = useNotification();

  // Use custom hook for robot type management
  const {
    robotTypesData,
    loadRobotTypes,
    createRobotType,
    updateRobotType,
    deleteRobotType,
    uploadRobotTypeIcon,
    pagination,
  } = useRobotTypeManagement();

  const { mappingsData, loadMappings } = useJoyControlMappingManagement();

  // Modal states
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedRobotType, setSelectedRobotType] = useState(null);

  // Form states
  const [newRobotType, setNewRobotType] = useState({
    name: "",
    description: "",
    iconFile: null,
  });
  const [editingRobotType, setEditingRobotType] = useState({
    name: "",
    description: "",
    iconFile: null,
    joy_control_mapping_id: null,
  });
  const [iconPreview, setIconPreview] = useState(null);

  const robotTypesTableSource = robotTypesData.results || [];

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadRobotTypes();
      loadMappings();
    }
  }, [authLoading, isAuthenticated, loadRobotTypes, loadMappings]);

  // Handle Add Modal
  const handleAddButtonClick = () => {
    setIsAddModalVisible(true);
  };

  const handleAddModalOk = async () => {
    try {
      const robotTypeData = {
        name: newRobotType.name,
        description: newRobotType.description || "",
      };

      const createdType = await createRobotType(robotTypeData);

      // Upload icon if user selected one
      if (newRobotType.iconFile && createdType) {
        await uploadRobotTypeIcon(createdType.type_id, newRobotType.iconFile);
      }

      setIsAddModalVisible(false);
      setNewRobotType({
        name: "",
        description: "",
        iconFile: null,
      });
      setIconPreview(null);
      notifySuccess("Robot type created successfully.");
      loadRobotTypes(); // Reload to show new robot type with icon
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
    setNewRobotType({
      name: "",
      description: "",
      iconFile: null,
    });
    setIconPreview(null);
  };

  // Handle Edit Modal
  const handleEditButtonClicked = (record) => {
    setSelectedRobotType(record);
    setEditingRobotType({
      name: record.name,
      description: record.description || "",
      iconFile: null,
      joy_control_mapping_id: record.joy_control_mapping?.mapping_id ?? null,
    });
    setIconPreview(null);
    setIsEditModalVisible(true);
  };

  const handleEditModalOk = async () => {
    const editingData = { ...editingRobotType };
    delete editingData.iconFile; // Don't send iconFile
    delete editingData.joy_control_mapping_id; // Handle separately

    // Only update if there are changes
    if (editingData.name === selectedRobotType.name) {
      delete editingData.name;
    }
    if (editingData.description === selectedRobotType.description) {
      delete editingData.description;
    }

    // Include joy_control_mapping_id only if it changed
    const originalMappingId = selectedRobotType.joy_control_mapping?.mapping_id ?? null;
    if (editingRobotType.joy_control_mapping_id !== originalMappingId) {
      editingData.joy_control_mapping_id = editingRobotType.joy_control_mapping_id;
    }

    const hasIconUpload = editingRobotType.iconFile !== null;
    const hasDataChanges = Object.keys(editingData).length > 0;

    if (!hasDataChanges && !hasIconUpload) {
      notifyError("No changes made to update.");
      setIsEditModalVisible(false);
      return;
    }

    try {
      // Update robot type data if there are changes
      if (hasDataChanges) {
        await updateRobotType(selectedRobotType.type_id, editingData);
      }

      // Upload icon if user selected one
      if (hasIconUpload) {
        await uploadRobotTypeIcon(
          selectedRobotType.type_id,
          editingRobotType.iconFile,
        );
      }

      setIsEditModalVisible(false);
      setEditingRobotType({
        name: "",
        description: "",
        iconFile: null,
        joy_control_mapping_id: null,
      });
      setIconPreview(null);
      setSelectedRobotType(null);
      notifySuccess("Robot type updated successfully.");
      loadRobotTypes(); // Reload to show updated icon
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setEditingRobotType({
      name: "",
      description: "",
      iconFile: null,
      joy_control_mapping_id: null,
    });
    setIconPreview(null);
    setSelectedRobotType(null);
  };

  // Handle Delete
  const handleDeleteButtonClicked = (typeId, typeName) => {
    Modal.confirm({
      title: `Are you sure you want to delete robot type "${typeName}"?`,
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        return await deleteRobotType(typeId);
      },
    });
  };

  // Handle icon file change
  const handleIconChange = (info, formType = "add") => {
    const file = info.file.originFileObj || info.file;

    // Validate file type
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return;
    }

    // Validate file size (5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return;
    }

    // Update the appropriate form state
    if (formType === "add") {
      setNewRobotType({ ...newRobotType, iconFile: file });
    } else {
      setEditingRobotType({ ...editingRobotType, iconFile: file });
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setIconPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Table columns
  const columns = [
    {
      title: "Icon",
      dataIndex: "icon_path",
      key: "icon_path",
      width: 80,
      render: (icon_path, record) => (
        <Avatar
          size={48}
          shape="square"
          src={
            icon_path
              ? servicesUrlConfig.ROBOTS_API.getStaticUrl(icon_path)
              : undefined
          }
          icon={!icon_path && <RobotOutlined />}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => <Text>{text || "-"}</Text>,
    },
    {
      title: "Type ID",
      dataIndex: "type_id",
      key: "type_id",
      render: (text) => (
        <Text type="secondary" copyable>
          {text}
        </Text>
      ),
    },
    {
      title: "Control Layout",
      dataIndex: "joy_control_mapping",
      key: "joy_control_mapping",
      render: (mapping) => <Text>{mapping?.name ?? "—"}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            ghost
            icon={<EditOutlined />}
            onClick={() => handleEditButtonClicked(record)}
          >
            Edit
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              handleDeleteButtonClicked(record.type_id, record.name)
            }
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24}>
          <Card
            variant="borderless"
            className="criclebox tablespace mb-24"
            title="Robot Types Management"
            extra={
              <Space>
                <Button
                  icon={<PlusOutlined />}
                  style={BUTTON_STYLES.add}
                  onClick={handleAddButtonClick}
                >
                  New Robot Type
                </Button>
                <Link to="/administrations/robot-joy-control-configs">
                  <Button icon={<ControlOutlined />} block>Manage Control Layout</Button>
                </Link>                
              </Space>
            }
          >
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={robotTypesTableSource}
                rowKey="type_id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onChange: (page, pageSize) => {
                    loadRobotTypes(page, pageSize);
                  },
                }}
                className="ant-border-space"
                onRow={(record) => ({
                  onDoubleClick: () => handleEditButtonClicked(record),
                })}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Add Robot Type Modal */}
      <Modal
        title="Add New Robot Type"
        open={isAddModalVisible}
        onOk={handleAddModalOk}
        onCancel={handleAddModalCancel}
        okText="Create"
        width={600}
      >
        <Form layout="vertical">
          <Form.Item
            label="Name"
            required
            validateStatus={newRobotType.name ? "" : "error"}
            help={!newRobotType.name ? "Please enter robot type name" : ""}
          >
            <Input
              placeholder="e.g., GoCart, Spot, Tracer"
              value={newRobotType.name}
              onChange={(e) =>
                setNewRobotType({ ...newRobotType, name: e.target.value })
              }
            />
          </Form.Item>

          <Form.Item label="Description">
            <TextArea
              rows={3}
              placeholder="Enter a description for this robot type"
              value={newRobotType.description}
              onChange={(e) =>
                setNewRobotType({
                  ...newRobotType,
                  description: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="Icon">
            <Upload
              beforeUpload={() => false}
              onChange={(info) => handleIconChange(info, "add")}
              maxCount={1}
              accept="image/*"
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Select Icon</Button>
            </Upload>
            {iconPreview && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Preview:</Text>
                <br />
                <Avatar size={64} shape="square" src={iconPreview} />
              </div>
            )}
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              Supported formats: PNG, JPG, JPEG, WebP (Max 5MB)
            </Text>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Robot Type Modal */}
      <Modal
        title="Edit Robot Type"
        open={isEditModalVisible}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        okText="Update"
        width={600}
      >
        <Form layout="vertical">
          {selectedRobotType && (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Current Icon:</Text>
              <br />
              <Avatar
                size={64}
                shape="square"
                src={
                  selectedRobotType.icon_path
                    ? servicesUrlConfig.ROBOTS_API.getStaticUrl(
                        selectedRobotType.icon_path,
                      )
                    : undefined
                }
                icon={!selectedRobotType.icon_path && <RobotOutlined />}
              />
            </div>
          )}

          <Form.Item label="Name" required>
            <Input
              placeholder="e.g., GoCart, Spot, Tracer"
              value={editingRobotType.name}
              onChange={(e) =>
                setEditingRobotType({
                  ...editingRobotType,
                  name: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="Description">
            <TextArea
              rows={3}
              placeholder="Enter a description for this robot type"
              value={editingRobotType.description}
              onChange={(e) =>
                setEditingRobotType({
                  ...editingRobotType,
                  description: e.target.value,
                })
              }
            />
          </Form.Item>

          <Form.Item label="Control Layout">
            <Select
              allowClear
              placeholder="None (use default layout)"
              value={editingRobotType.joy_control_mapping_id ?? undefined}
              onChange={(val) =>
                setEditingRobotType({
                  ...editingRobotType,
                  joy_control_mapping_id: val ?? null,
                })
              }
              options={(mappingsData.results || []).map((m) => ({
                value: m.mapping_id,
                label: m.name,
              }))}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item label="Upload New Icon (Optional)">
            <Upload
              beforeUpload={() => false}
              onChange={(info) => handleIconChange(info, "edit")}
              maxCount={1}
              accept="image/*"
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Select New Icon</Button>
            </Upload>
            {iconPreview && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">New Icon Preview:</Text>
                <br />
                <Avatar size={64} shape="square" src={iconPreview} />
              </div>
            )}
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              Supported formats: PNG, JPG, JPEG, WebP (Max 5MB)
            </Text>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RobotTypesManagement;
